import { axiosInstance } from "lib/axios";
import { searchAPI } from "services/apis.service";
import { resolveDatabaseTimeframe } from "utils/database-timeframe.util";
import {
  ebscoLabel,
  proQuestLabel,
} from "features/SourceSearch/data/search-fields.data";

const databaseDisplayNames = {
  Ebsco: ebscoLabel,
  ProQuest: proQuestLabel,
};

export const getDatabaseApiName = (database) => {
  const normalizedDatabase = String(database).toLowerCase();
  if (normalizedDatabase === "ebsco") return "Ebsco";
  if (normalizedDatabase === "proquest") return "ProQuest";
  return database;
};

export const getDatabaseDisplayName = (database) => {
  const apiName = getDatabaseApiName(database);
  return databaseDisplayNames[apiName] || database;
};

const getWebPages = (section) => {
  const webPages = section?.webPages || section?.data?.webPages || section?.Data?.webPages;
  if (!webPages) return webPages;
  const pagination = section?.pagination || section?.data?.pagination || section?.Data?.pagination || webPages.pagination;
  return {
    ...webPages,
    totalEstimatedMatches: pagination?.totalCount ?? webPages.totalEstimatedMatches,
  };
};

export const normalizeDatabaseSearchSections = (data, databases) => {
  if (Array.isArray(data?.results)) {
    return data.results.map((result) => ({
      ...result,
      media: getDatabaseDisplayName(result.media || result.database || result.databaseName),
      webPages: getWebPages(result) || result.webPages,
    }));
  }

  if (Array.isArray(data)) {
    return data.map((result) => ({
      ...result,
      media: getDatabaseDisplayName(result.media || result.database || result.databaseName),
      webPages: getWebPages(result) || result.webPages,
    }));
  }

  if (data?.webPages) {
    const database = databases?.[0];
    return [
      {
        media: getDatabaseDisplayName(database),
        webPages: getWebPages(data),
      },
    ];
  }

  return (databases || [])
    .map((database) => {
      const section = data?.[database] || data?.[getDatabaseDisplayName(database)];
      const webPages = getWebPages(section) || section?.webPages;
      if (!webPages) return null;
      return {
        ...section,
        media: getDatabaseDisplayName(database),
        webPages,
      };
    })
    .filter(Boolean);
};

export const getDatabaseSearch = async (
  searchTerm,
  timePeriod,
  databases,
  pageNumber,
  pageSize = 21
) => {
  const databaseNames = (databases || []).map(getDatabaseApiName);
  if (!databaseNames.length) return { results: [] };
  const request = { searchTerm, databases: databaseNames, pageNumber, pageSize };

  if (databaseNames.includes("Ebsco")) {
    const ebscoTimePeriod = resolveDatabaseTimeframe("Ebsco", timePeriod).timePeriod;
    // Other databases keep the original selection when EBSCO needs a fallback.
    if (databaseNames.length > 1 && (ebscoTimePeriod !== timePeriod || !ebscoTimePeriod)) {
      const results = await Promise.all(databaseNames.map(async (database) => {
        const data = await getDatabaseSearch(searchTerm, timePeriod, [database], pageNumber, pageSize);
        return normalizeDatabaseSearchSections(data, [database]);
      }));
      return { results: results.flat() };
    }
    if (ebscoTimePeriod) request.timePeriod = ebscoTimePeriod;
  } else {
    request.timePeriod = timePeriod;
  }

  const { data } = await axiosInstance.post(searchAPI.databaseSearch, request);
  return data.data;
};
